/**
 * Google reCAPTCHA v3 in the browser. Version 3 is invisible: there is no puzzle and no user interaction, only
 * a short-lived token the page mints for a named action and hands to the API, which is the only side that can
 * turn it into a score. Nothing here is a secret — the site key is public — and nothing here decides anything:
 * a token that cannot be produced simply is not sent, and the API refuses the request on its own terms.
 */

interface GoogleRecaptcha {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GoogleRecaptcha;
  }
}

export interface RecaptchaSettings {
  readonly siteKey: string;
  readonly scriptHost: string;
}

/**
 * The script host arrives from an API response, so it is checked against this list before a script element is
 * ever created. `recaptcha.net` is Google's own alternative for networks that cannot reach `google.com`.
 */
const SUPPORTED_HOSTS: readonly string[] = ['www.google.com', 'www.recaptcha.net'];

const SCRIPT_ELEMENT_ID = 'varza-recaptcha-api';

/** Long enough for a cold script load on a slow connection, short enough that a form is never simply stuck. */
const EXECUTION_TIMEOUT_MS = 10_000;

let settings: RecaptchaSettings | null = null;
let apiPromise: Promise<GoogleRecaptcha> | undefined;

/** Applies the settings the API published, or disables the client entirely when they are unusable. */
export function configureRecaptcha(next: RecaptchaSettings | null): void {
  const usable = next !== null && next.siteKey.length > 0 && SUPPORTED_HOSTS.includes(next.scriptHost)
    ? next
    : null;

  if (usable?.siteKey === settings?.siteKey && usable?.scriptHost === settings?.scriptHost) return;

  settings = usable;
  apiPromise = undefined;
  document.getElementById(SCRIPT_ELEMENT_ID)?.remove();
}

/** Drops the loaded script and the cached settings. Tests use it; the application never needs to. */
export function resetRecaptcha(): void {
  settings = null;
  apiPromise = undefined;
  document.getElementById(SCRIPT_ELEMENT_ID)?.remove();
}

export function isRecaptchaConfigured(): boolean {
  return settings !== null;
}

/**
 * Warms the script so the first protected submission does not also pay for the download. Failure is ignored:
 * this is an optimisation, and the real attempt will try again.
 */
export function preloadRecaptcha(): void {
  if (settings === null) return;
  void loadApi().catch(() => undefined);
}

/**
 * Mints one token for one action. Returns `null` when reCAPTCHA is switched off, unreachable, or blocked by
 * the network — the request then travels without a token and the API decides, which keeps the judgement on
 * the side that cannot be tampered with.
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  const current = settings;
  if (current === null) return null;

  try {
    const api = await withTimeout(loadApi());
    const token = await withTimeout(api.execute(current.siteKey, { action }));
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

function loadApi(): Promise<GoogleRecaptcha> {
  if (apiPromise !== undefined) return apiPromise;

  const current = settings;
  if (current === null) return Promise.reject(new Error('reCAPTCHA is not configured.'));

  apiPromise = injectScript(current).then(ready).catch((error: unknown) => {
    // A failed load is not remembered, so a form submitted after the network recovers can succeed.
    apiPromise = undefined;
    throw error;
  });
  return apiPromise;
}

function injectScript(current: RecaptchaSettings): Promise<void> {
  if (window.grecaptcha !== undefined) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ELEMENT_ID);
    if (existing !== null) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ELEMENT_ID;
    script.src = `https://${current.scriptHost}/recaptcha/api.js?render=${encodeURIComponent(current.siteKey)}`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load.')), { once: true });
    document.head.appendChild(script);
  });
}

/** `grecaptcha.ready` is how the library says its own initialization has finished. */
function ready(): Promise<GoogleRecaptcha> {
  const api = window.grecaptcha;
  if (api === undefined) return Promise.reject(new Error('reCAPTCHA did not initialize.'));
  return new Promise((resolve) => api.ready(() => resolve(api)));
}

function withTimeout<T>(work: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('reCAPTCHA timed out.')), EXECUTION_TIMEOUT_MS);
    work.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}
